import React, { memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useFormikContext } from 'formik'
import { Button } from 'src/components/shared/Button'
import { SectionCard } from 'src/components/shared/SectionCard'
import type { CollectDataStepType } from 'src/constants/collect-data/enums'
import type { AiDocumentReview, ReviewFormValues } from 'src/interfaces/documentReview'
import {
  applyAiMatchedDataPrefill,
  buildAiMatchedValueRows,
  formatReasonComparisonValue,
  hasReasonComparison,
  resolveAiReasonTitle,
} from 'src/utils/documentReviewAi'

interface Props {
  aiDocumentReview: AiDocumentReview | null
  /** Picks which matched values are listed and where "Use these values" puts the document ID. */
  stepType: CollectDataStepType
}

const decisionTitles: Record<string, string> = {
  approve: 'Looks good',
  decline: 'Needs a closer look',
  needs_human_review: 'Needs a human decision',
}

const severityStyles = {
  blocking: tw`text-critical-500`,
  warning: tw`text-warning-500`,
  info: tw`text-shades-500`,
}

/**
 * The AI review, as the backend decided it.
 *
 * The verdict wording comes from the real `decision` field, never from a threshold on `slpRate` —
 * no such threshold exists in the backend, which is also why the queue column shows the raw score
 * instead (see the design's §5.3).
 */
const AiReviewCardComponent = ({ aiDocumentReview, stepType }: Props) => {
  const { setValues } = useFormikContext<ReviewFormValues>()

  const matchedData = aiDocumentReview?.matchedData

  const matchedValueRows = useMemo(() => {
    return buildAiMatchedValueRows(stepType, matchedData)
  }, [matchedData, stepType])

  const handlePrefill = useCallback(() => {
    if (!matchedData) {
      return
    }

    setValues((previousValues) => {
      return applyAiMatchedDataPrefill({ stepType, matchedData, values: previousValues })
    })
  }, [matchedData, setValues, stepType])

  if (!aiDocumentReview) {
    return (
      <SectionCard title="AI review" dataTestId="document-review-ai-card">
        <Typography variant="body2" css={tw`text-shades-500`}>
          The AI check did not run for this document.
        </Typography>
      </SectionCard>
    )
  }

  const decisionTitle = aiDocumentReview.decision
    ? decisionTitles[aiDocumentReview.decision] ?? aiDocumentReview.decision
    : 'No decision recorded'

  return (
    <SectionCard
      title="AI review"
      subtitle={
        aiDocumentReview.mode === 'shadow'
          ? 'Shadow mode — this decision was recorded but did not act on the step.'
          : undefined
      }
      dataTestId="document-review-ai-card"
    >
      <div css={tw`flex flex-col gap-4`}>
        <div css={tw`flex flex-row items-center gap-3`}>
          <Typography variant="h4">{decisionTitle}</Typography>
          {aiDocumentReview.slpRate === undefined || aiDocumentReview.slpRate === null ? null : (
            <Typography variant="body2" css={tw`text-shades-500`}>
              Score {aiDocumentReview.slpRate.toFixed(2)}
            </Typography>
          )}
        </div>

        {aiDocumentReview.decisionReasons && aiDocumentReview.decisionReasons.length > 0 ? (
          <div css={tw`flex flex-col gap-2`}>
            {aiDocumentReview.decisionReasons.map((reason) => {
              return (
                <div key={`${reason.code}-${reason.message}`} css={tw`flex flex-col`}>
                  <Typography
                    variant="body2"
                    css={[tw`font-semibold`, severityStyles[reason.severity]]}
                  >
                    {resolveAiReasonTitle(reason.code)}
                  </Typography>
                  <Typography variant="body2" css={tw`text-shades-500`}>
                    {reason.message}
                  </Typography>
                  {hasReasonComparison(reason) ? (
                    <Typography variant="body2" css={tw`text-xs text-shades-400`}>
                      Expected: {formatReasonComparisonValue(reason.expected)} · Provided:{' '}
                      {formatReasonComparisonValue(reason.actual)}
                    </Typography>
                  ) : null}
                </div>
              )
            })}
          </div>
        ) : null}

        {matchedValueRows.length > 0 ? (
          <div css={tw`flex flex-col gap-1`} data-testid="document-review-ai-matched-values">
            <Typography variant="body2" css={tw`font-semibold`}>
              Matched values
            </Typography>
            {matchedValueRows.map((row) => {
              return (
                <div key={row.label} css={tw`flex flex-row gap-2`}>
                  <Typography variant="body2" css={tw`w-48 shrink-0 text-shades-500`}>
                    {row.label}
                  </Typography>
                  <Typography variant="body2" css={tw`break-all text-shades-900`}>
                    {row.value}
                  </Typography>
                </div>
              )
            })}
          </div>
        ) : null}

        {aiDocumentReview.extractedPeople && aiDocumentReview.extractedPeople.length > 0 ? (
          <div css={tw`flex flex-col gap-1`}>
            <Typography variant="body2" css={tw`font-semibold`}>
              People named on this document
            </Typography>
            {aiDocumentReview.extractedPeople.map((person, index) => {
              return (
                <Typography
                  key={`${person.firstName}-${person.lastName}-${index}`}
                  variant="body2"
                  css={person.isMatched ? tw`text-shades-900` : tw`text-shades-500`}
                >
                  {[person.firstName, person.lastName].filter(Boolean).join(' ')}
                  {person.isMatched ? ' — matches the contractor on file' : ''}
                </Typography>
              )
            })}
          </div>
        ) : null}

        {aiDocumentReview.extractedOrganizations &&
        aiDocumentReview.extractedOrganizations.length > 0 ? (
          <div css={tw`flex flex-col gap-1`}>
            <Typography variant="body2" css={tw`font-semibold`}>
              Organizations named
            </Typography>
            {aiDocumentReview.extractedOrganizations.map((organization, index) => {
              return (
                <Typography
                  key={`${organization.name}-${index}`}
                  variant="body2"
                  css={organization.isMatched ? tw`text-shades-900` : tw`text-shades-500`}
                >
                  {organization.name ?? ''}
                </Typography>
              )
            })}
          </div>
        ) : null}

        {aiDocumentReview.extractedVehicles && aiDocumentReview.extractedVehicles.length > 0 ? (
          <div css={tw`flex flex-col gap-1`}>
            <Typography variant="body2" css={tw`font-semibold`}>
              Vehicles named
            </Typography>
            {aiDocumentReview.extractedVehicles.map((vehicle, index) => {
              const vehicleLabel = [vehicle.year, vehicle.make, vehicle.model, vehicle.vin]
                .filter(Boolean)
                .join(' ')

              return (
                <Typography
                  key={vehicle.vin ?? index}
                  variant="body2"
                  css={vehicle.isMatched ? tw`text-shades-900` : tw`text-shades-500`}
                >
                  {vehicleLabel}
                </Typography>
              )
            })}
          </div>
        ) : null}

        {/* `prefillSafe` is the backend's answer, not a rule this component re-derives. When it is
            false, something went unconfirmed and the reviewer reads the document themselves. */}
        {aiDocumentReview.prefillSafe && matchedData ? (
          <div>
            <Button
              size="small"
              color="white"
              onClick={handlePrefill}
              data-testid="document-review-ai-prefill-button"
            >
              Use these values
            </Button>
          </div>
        ) : null}
      </div>
    </SectionCard>
  )
}

export const AiReviewCard = memo(AiReviewCardComponent)
